<template>
  <v-container fluid>
    <v-card>
      <v-card-text>
        <v-row>
          <v-col cols="12" lg="6">
            <v-expansion-panels popout class="mb-3">
              <v-expansion-panel>
                <template #header>
                  <div class="text-h6">Installed Capacity By Fuel Type</div>
                </template>
                <ChartYearly visible type="doughnut" v-bind="generatorChart"></ChartYearly>
              </v-expansion-panel>
            </v-expansion-panels>
            <p class="mx-16">Source: AA</p>
          </v-col>
          <v-col cols="12" sm="6" v-for="(data, index) in charts[0]" :key="`${index}${data}`">
            <Plotly
              :data="data"
              :layout="charts[1][index]"
              :toImageButtonOptions="chartDownloadName(caseName, 'IRP', charts[1][index].title)"
            />
            <p class="mx-16">Source: {{ charts[1][index].output }}</p>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>
<script>
import chartUtils from "@/mixins/chartUtils";
import fieldUtils from "@/mixins/fieldUtils";
import PCDashboardMixins from "@/mixins/PCDashboard";
import { chartDownloadName, createAxis, filterLowChartValues } from "@/plugins/utils";

const colorSet = {
  URANIUM: "#FFA500",
  COAL: "#000000",
  HYDRO: "#00008B",
  WIND: "#008000",
  DISTILLATE_FUEL_OIL: "#696969",
  SOLAR_PV: "#FFD700",
  NATURAL_GAS: "#D3D3D3",
  HYDRO_ROR: "#0000FF",
};

export default {
  name: "OutputIRPDashboard",
  mixins: [fieldUtils, chartUtils, PCDashboardMixins],
  components: {
    ChartYearly: () => import("@/components/charts/ChartYearly.vue"),
    Plotly: () => import("@/components/charts/Plotly.vue"),
  },
  props: {
    caseOutputs: { type: Object, required: false, default: () => ({}) },
    inputCase: { type: Object, required: false, default: () => ({}) },
    num_years: {
      type: Number,
      required: false,
      default: null,
    },
    logoUrl: {
      type: String,
      required: false,
      default: "https://acelerex.com/wp-content/uploads/2021/01/rex-logo.da3e6933.png",
    },
  },
  data() {
    const logo = {
      xanchor: "right",
      xref: "paper",
      yanchor: "middle",
      yref: "paper",
      layer: "above",
      x: 1,
      y: 0.1,
      sizex: 0.2,
      sizey: 0.4,
      opacity: 0.8,
      source: this.logoUrl,
    };
    return {
      caseName: this.inputCase.name,
      AAOutputs: this.caseOutputs.ALTERNATIVE_ANALYSIS,
      PCOutputs: this.caseOutputs.PRODUCTION_COST,
      layouts: [
        {
          visible: null,
          barmode: "relative",
          title: "Installed Capacity",
          output: "AA",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "Capacity (MW)",
            },
          },
          showlegend: true,
          legend: { orientation: "h" },
          images: [logo],
        },
        {
          visible: null,
          barmode: "relative",
          title: "Change In Installed Capacity",
          output: "AA",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "MW",
            },
          },
          showlegend: true,
          legend: { orientation: "h" },
          images: [logo],
        },
        {
          visible: null,
          barmode: "relative",
          title: "Fixed Operation and Maintenance Cost",
          output: "AA",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "FOM Cost (M$)",
            },
          },
          showlegend: true,
          legend: { orientation: "h" },
          images: [logo],
        },
        {
          visible: null,
          barmode: "relative",
          title: "Variable Operation and Maintenance Cost",
          output: "PC",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "M$",
            },
          },
          showlegend: true,
          legend: { orientation: "h" },
          images: [logo],
        },
        {
          visible: null,
          barmode: "relative",
          title: "Total Costs",
          output: "AA, PC",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "M$",
            },
          },
          showlegend: true,
          legend: { orientation: "h" },
          images: [logo],
        },

        {
          visible: null,
          barmode: "relative",
          title: "Generation By Fuel Type",
          output: "PC",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "Generation (GWh)",
            },
          },
          showlegend: true,
          legend: { orientation: "h" },
          images: [logo],
        },
        {
          visible: null,
          barmode: "relative",
          title: "Generation Share By Fuel Type",
          output: "PC",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "%",
            },
          },
          showlegend: true,
          legend: { orientation: "h" },
          images: [logo],
        },

        {
          visible: null,
          barmode: "relative",
          title: "Current Fleet Retirements",
          output: "AA",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "MW",
            },
          },
          showlegend: true,
          legend: { orientation: "h" },
          images: [logo],
        },
        {
          visible: null,
          title: "CO2 Emissions",
          output: "PC",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "Ton",
            },
          },
          showlegend: true,
          legend: { orientation: "h" },
          images: [logo],
        },
      ],
    };
  },
  mounted() {},
  computed: {
    simulationYearArray() {
      const size = this.inputCase.settings.year_end - this.inputCase.settings.year_start;
      const yearArray = Array(1).fill(this.inputCase.settings.year_start);
      for (let i = 0; i < size; i += 1) {
        yearArray[i + 1] = yearArray[i] + 1;
      }
      return yearArray;
    },
    generatorChart() {
      const generators = this.AAOutputs.generators.map(this.mapToValue("installed_capacity"));
      return this.pieObjectByField("pie", generators, "source");
    },
    generationByFuelType() {
      return this.PCOutputs.generators[0].annual_generation
        ? this.getGeneratorData(this.PCOutputs, "annual_generation", { scaleNumber: 1 / 1000 })
        : this.getReducedData(this.PCOutputs.fuel_types, "type", "generation");
    },
    changeInInstalledCapacity() {
      const eceonomicRetirements = this.getGeneratorData(this.AAOutputs, "economic_retirement");
      const forcedDeactivation = this.getGeneratorData(this.AAOutputs, "forced_deactivation");
      const forcedBuild = this.getGeneratorData(this.AAOutputs, "forced_build");
      const economicBuild = this.getGeneratorData(this.AAOutputs, "economic_build");
      return Object.keys(economicBuild).reduce((acc, source) => {
        if (!acc[source]) {
          acc[source] = economicBuild[source].map(
            (el, index) =>
              el +
              forcedBuild[source][index] -
              eceonomicRetirements[source][index] -
              forcedDeactivation[source][index]
          );
        }
        return acc;
      }, {});
    },
    currentFleetRetirements() {
      const eceonomicRetirements = this.getGeneratorData(this.AAOutputs, "economic_retirement");
      const forcedDeactivation = this.getGeneratorData(this.AAOutputs, "forced_deactivation");
      return Object.keys(eceonomicRetirements).reduce((acc, source) => {
        if (!acc[source]) {
          acc[source] = eceonomicRetirements[source].map(
            (el, index) => el + forcedDeactivation[source][index]
          );
        }
        return acc;
      }, {});
    },
    co2EmissionChart() {
      return [
        {
          name: "CO2 Emission",
          ...this.createAxis(
            this.getOutput("co2_emissions", this.PCOutputs),
            this.simulationYearArray
          ),
          type: "scatter",
        },
      ];
    },
    productionCostChart() {
      const data4 = [
        {
          label: "FOM Cost",
          data: this.getOutput("fom_cost", this.PCOutputs, { scaleNumber: 1 / 1000000 }),
        },
        {
          label: "VOM Cost",
          data: this.getOutput("vom_cost", this.PCOutputs, { scaleNumber: 1 / 1000000 }),
        },
        {
          label: "Fuel Cost",
          data: this.getOutput("fuel_cost", this.PCOutputs, { scaleNumber: 1 / 1000000 }),
        },
        {
          label: "Emission Cost",
          data: this.getOutput("co2_emissions_cost", this.PCOutputs, { scaleNumber: 1 / 1000000 }),
        },
      ].map((dataset) => ({
        ...this.createAxis(dataset.data, this.simulationYearArray),
        name: dataset.label,
        type: "bar",
      }));
      data4[data4.length] = {
        name: "Generation Cost",
        mode: "lines+markers",
        marker: {
          color: "#000000",
          size: 6,
        },
        line: {
          color: "#000000",
          width: 2,
        },
        ...this.createAxis(
          this.getOutput("generation_cost", this.PCOutputs, { scaleNumber: 1 / 1000000 }),
          this.simulationYearArray
        ),
      };
      return data4;
    },
    generationPercentage() {
      const totalPercentbyYear = [];
      const copyObj = { ...this.generationByFuelType };
      this.generationByFuelType[Object.keys(this.generationByFuelType)[0]].forEach((arr, index) => {
        totalPercentbyYear.push(
          Object.values(this.generationByFuelType).reduce(
            (acc, current) => acc[index] + current[index]
          )
        );
      });
      return Object.keys(copyObj).reduce((acc, source) => {
        if (!acc[source]) {
          acc[source] = [];
        }
        acc[source] = copyObj[source].map((el, index) => (el * 100) / totalPercentbyYear[index]);
        return acc;
      }, {});
    },
    totalData() {
      return [
        this.stackedAreaChart(this.getGeneratorData(this.AAOutputs, "installed_capacity")),
        this.stackedColumnChart(this.changeInInstalledCapacity),
        this.stackedColumnChart(
          this.getGeneratorData(this.AAOutputs, "fom_cost", { scaleNumber: 1 / 1000000 })
        ),
        this.stackedColumnChart(
          this.getGeneratorData(this.PCOutputs, "vom_cost", { scaleNumber: 1 / 1000000 })
        ),
        this.productionCostChart,
        this.stackedAreaChart(this.generationByFuelType),
        this.stackedColumnChart(this.generationPercentage),
        this.stackedColumnChart(this.currentFleetRetirements),
        this.co2EmissionChart,
      ];
    },
    charts() {
      this.totalData.forEach((data, index) => {
        if (data.length > 0) {
          this.layouts[index].visible = true;
        } else {
          this.layouts[index].visible = false;
        }
      });

      return [
        this.totalData.filter((chart) => chart.length > 0),
        this.layouts.filter((layout) => layout.visible === true),
      ];
    },
  },
  methods: {
    filterLowChartValues,
    chartDownloadName,
    createAxis,
    stackedColumnChart(data) {
      const sum = Object.values(data)
        .map((el) => el.reduce((acc, item) => (item < 0 ? acc - item : acc + item), 0))
        .reduce((acc, item) => acc + item, 0);
      return Object.values(data)
        .map((item, index) => ({
          marker: {
            color: colorSet[Object.keys(data)[index]],
          },

          ...this.createAxis(item, this.simulationYearArray),
          name: this.formatString(Object.keys(data)[index]),
          type: "bar",
        }))
        .filter(filterLowChartValues(sum * 0.001));
    },
    stackedAreaChart(data) {
      const sum = Object.values(data)
        .map((el) => el.reduce((acc, item) => (item < 0 ? acc - item : acc + item), 0))
        .reduce((acc, item) => acc + item, 0);
      return Object.values(data)
        .map((item, index) => ({
          fillcolor: colorSet[Object.keys(data)[index]],
          ...this.createAxis(item, this.simulationYearArray),
          name: this.formatString(Object.keys(data)[index]),
          stackgroup: "one",
          mode: "none",
        }))
        .filter(filterLowChartValues(sum * 0.001));
    },
    getReducedData(output, key, parameter) {
      return Object.values(output).reduce((acc, obj) => {
        if (!acc[obj[key]]) {
          acc[obj[key]] = Object.values(obj[parameter]);
        }
        return acc;
      }, {});
    },
    getGeneratorData(output, parameter, { scaleNumber = null } = {}) {
      return Object.values(output.generators).reduce((acc, obj) => {
        const key = obj.source;
        if (!acc[key]) {
          if (scaleNumber) {
            acc[key] = Object.values(obj[parameter]).map((el) => el * scaleNumber);
          } else {
            acc[key] = Object.values(obj[parameter]);
          }
        } else if (scaleNumber) {
          acc[key] = Object.values(acc[key]).map(
            (el, index) => el + Object.values(obj[parameter])[index] * scaleNumber
          );
        } else {
          acc[key] = Object.values(acc[key]).map(
            (el, index) => el + Object.values(obj[parameter])[index]
          );
        }
        return acc;
      }, {});
    },
    getOutput(name, inout, { scaleNumber = null } = {}) {
      if (typeof name === "string") {
        if (scaleNumber) {
          return Object.values(inout[name]).map((el) => el * scaleNumber) || [];
        }
        return inout[name] || [];
      }
      if (typeof name === "function") {
        return name(inout);
      }
      throw Error(`Invalid output key ${name}`);
    },
  },
};
</script>
<style></style>
